"use client";

import React, { useId, useMemo, useRef, useState } from "react";

function IconCheck({ className = "" }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			className={className}
		>
			<path
				d="M5 13L9 17L19 7"
				stroke="#138808"
				strokeWidth="2.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function IconCross({ className = "" }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="20"
			height="20"
			viewBox="0 0 20 20"
			fill="none"
			className={className}
		>
			<path
				d="M5 15L15 5M5 5L15 15"
				stroke="#DC143C"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function IconChevronLeft({ className = "" }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="20"
			height="20"
			viewBox="0 0 20 20"
			fill="none"
			className={className}
		>
			<path
				d="M9.16732 15.8334L3.33398 10.0001L9.16732 4.16675M15.834 15.8334L10.0007 10.0001L15.834 4.16675"
				stroke="currentColor"
				strokeWidth="1.33"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function IconChevronRight({ className = "" }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="20"
			height="20"
			viewBox="0 0 20 20"
			fill="none"
			className={className}
		>
			<path
				d="M10.8327 4.16675L16.666 10.0001L10.8327 15.8334M4.16602 4.16675L9.99935 10.0001L4.16602 15.8334"
				stroke="currentColor"
				strokeWidth="1.33"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function clampIndex(i, len) {
	const n = len || 1;
	return ((i % n) + n) % n;
}

function CellContent({ cell }) {
	if (!cell) return null;

	if (cell.type === "bool") {
		const yes = cell.value === true;
		return (
			<div className="cellBool">
				{yes ? <IconCheck className="iconYes" /> : <IconCross className="iconNo" />}
				<span className="boolText">{yes ? "Tak" : "Nie"}</span>
				{cell.sub ? <div className="sub">{cell.sub}</div> : null}
			</div>
		);
	}

	return (
		<div className="cellText">
			<div className="main">{cell.main}</div>
			{cell.sub ? <div className="sub">{cell.sub}</div> : null}
		</div>
	);
}

export default function Dn1ComparisonTable({ initialColumn = 0 }) {
	const tableId = useId();
	const liveRegionId = useId();
	const wrapperRef = useRef(null);

	const OPTIONS = useMemo(
		() => [
			{ title: "Kalkulator DN-1 online (Przyjazne Deklaracje)" },
			{ title: "Formularz DN-1 online (np. ePUAP)" },
			{ title: "DN-1 druk aktywny / PDF interaktywny (np. GOFIN)" },
		],
		[],
	);

	const ROWS = useMemo(
		() => [
			{
				label: "Automatyczne wprowadzanie danych z CEIDG / GUS",
				cells: [
					{ type: "bool", value: true },
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Pracochłonność przygotowania pierwszej deklaracji",
				cells: [
					{ type: "text", main: "Niska / Średnia", sub: "Poniżej 1 h*" },
					{ type: "text", main: "Wysoka", sub: "6-8 h*" },
					{ type: "text", main: "Średnia/Wysoka", sub: "4-6 h*" },
				],
			},
			{
				label: "Zaczytywanie danych z uprzednio złożonej deklaracji w celu przygotowania korekty",
				cells: [
					{ type: "bool", value: true },
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Pracochłonność przygotowania kolejnej deklaracji",
				cells: [
					{
						type: "text",
						main: "Niska",
						sub: "5-30 min*, ze względu na możliwość edytowania / poprawiania danych po wczytaniu pliku DEK / Excel",
					},
					{ type: "text", main: "Wysoka", sub: "6-10 h*" },
					{ type: "text", main: "Średnia", sub: "2-4 h*" },
				],
			},
			{
				label: "Podatność na błędy w obliczeniach",
				cells: [
					{ type: "text", main: "Niska", sub: "Obliczenia i sumy po stronie systemu" },
					{
						type: "text",
						main: "Wysoka",
						sub: "Formularz nie liczy i nie sumuje automatycznie",
					},
					{
						type: "text",
						main: "Wysoka",
						sub: "Druk aktywny nie sumuje automatycznie",
					},
				],
			},
			{
				label: "Wyszukanie odpowiedniej uchwały i automatyczny dobór stawek",
				cells: [
					{ type: "bool", value: true },
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Automatyczne obliczenie wysokości podatku",
				cells: [
					{ type: "bool", value: true },
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Automatyczne obliczenie wysokości rat",
				cells: [
					{
						type: "bool",
						value: true,
						sub: "Kalkulator liczy raty dla większości gmin i miast",
					},
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Automatyczne sumowanie danych z załączników do deklaracji głównej",
				cells: [
					{ type: "bool", value: true },
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Automatyczny dobór załączników i uzupełnienie załączników (dla DN-1: ZDN-1/ZDN-2)",
				cells: [
					{
						type: "bool",
						value: true,
						sub: "Na podstawie uzupełnionych danych system automatycznie wygeneruje załączniki",
					},
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Konieczność znajomości zasad wyliczenia podatku",
				cells: [
					{
						type: "text",
						main: "Niska / nie wymagana",
						sub: "System prowadzi krok po kroku i podpowiada, trzeba jednak znać swoje dane",
					},
					{ type: "text", main: "Wymagana wysoka" },
					{ type: "text", main: "Wymagana wysoka" },
				],
			},
			{
				label: "Możliwość złożenia deklaracji online",
				cells: [
					{ type: "bool", value: true },
					{ type: "bool", value: true, sub: "np. ePUAP" },
					{
						type: "bool",
						value: false,
						sub: "Brak możliwości wygenerowania XML, który jest formalną wersją elektroniczną",
					},
				],
			},
			{
				label: "Możliwość wczytania danych z pliku Excel",
				cells: [
					{ type: "bool", value: true },
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Wyszukanie danych przez wybranie działki z mapy",
				cells: [
					{
						type: "bool",
						value: true,
						sub: "Kalkulator liczy raty dla większości gmin i miast",
					},
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Wyszukanie danych z EKW (elektronicznej księgi wieczystej)",
				cells: [
					{ type: "bool", value: true, sub: "Dla części miast i gmin" },
					{ type: "bool", value: false },
					{ type: "bool", value: false },
				],
			},
			{
				label: "Deklaracja DN-1 edytowalna w celu złożenia korekty lub deklaracji w kolejnym roku",
				cells: [
					{
						type: "bool",
						value: true,
						sub: "Wczytanie pliku DEK / Excel lub oba jednocześnie",
					},
					{ type: "bool", value: false },
					{ type: "bool", value: true },
				],
			},
		],
		[],
	);

	const [selectedCol, setSelectedCol] = useState(() => clampIndex(initialColumn, OPTIONS.length));
	const selectedTitle = OPTIONS[selectedCol]?.title || "";

	function selectColumn(nextIndex) {
		if (nextIndex < 0 || nextIndex >= OPTIONS.length) return;
		setSelectedCol(nextIndex);
	}

	return (
		<section className="dn1Wrap" ref={wrapperRef}>
			<div className="desktopOnly">
				<div
					className="tableOuter"
					role="region"
					aria-label="Porównanie sposobów przygotowania Deklaracji DN-1"
					tabIndex={0}
				>
					<table className="cmpTable" id={tableId}>
						<caption className="srOnly">
							Porównanie: Kalkulator DN-1 online (Przyjazne Deklaracje), Formularz DN-1 online (np.
							ePUAP), DN-1 druk aktywny / PDF interaktywny (np. GOFIN)
						</caption>

						<thead>
							<tr>
								<th scope="col" className="critHead">
									<span className="srOnly">Kryterium</span>
								</th>

								{OPTIONS.map((opt, i) => {
									const isSel = i === 0;
									return (
										<th
											key={opt.title}
											scope="col"
											className={`optHead ${isSel ? "sel" : ""}`}
											data-selected={isSel ? "true" : "false"}
										>
											<div className="headBtn">{opt.title}</div>
										</th>
									);
								})}
							</tr>
						</thead>

						<tbody>
							{ROWS.map((row, rIdx) => {
								const rowPos = rIdx === 0 ? "first" : rIdx === ROWS.length - 1 ? "last" : "mid";

								return (
									<tr key={row.label}>
										<th scope="row" className="critCell">
											{row.label}
										</th>

										{row.cells.map((cell, cIdx) => {
											const isSel = cIdx === 0;
											return (
												<td
													key={`${row.label}-${cIdx}`}
													className={`optCell ${isSel ? "sel" : ""}`}
													data-selected={isSel ? "true" : "false"}
													data-rowpos={rowPos}
												>
													<CellContent cell={cell} />
												</td>
											);
										})}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>

			<div className="mobileOnly">
				<div className="switcher" role="group" aria-label="Zmiana kolumny porównania">
					<button
						type="button"
						className="arrowBtn"
						onClick={() => selectColumn(selectedCol - 1)}
						aria-label="Poprzednia kolumna"
						aria-controls={tableId}
						disabled={selectedCol === 0}
					>
						<IconChevronLeft />
					</button>

					<div className="switchTitleWrap">
						<div className="switchTitle" aria-hidden="true">
							{selectedTitle}
						</div>
						<div id={liveRegionId} className="srOnly" aria-live="polite">
							{selectedTitle}
						</div>
					</div>

					<button
						type="button"
						className="arrowBtn"
						onClick={() => selectColumn(selectedCol + 1)}
						aria-label="Następna kolumna"
						aria-controls={tableId}
						disabled={selectedCol === OPTIONS.length - 1}
					>
						<IconChevronRight />
					</button>
				</div>

				<div
					className="tableOuter"
					role="region"
					aria-label="Porównanie sposobów przygotowania Deklaracji DN-1"
					tabIndex={0}
				>
					<table className="cmpTable mobile" id={tableId}>
						<caption className="srOnly">Porównanie: {selectedTitle}</caption>

						<thead>
							<tr>
								<th scope="col" className="critHead">
									<span className="srOnly">Kryterium</span>
								</th>
								<th
									scope="col"
									className={`optHead ${selectedCol === 0 ? "sel" : ""}`}
									data-selected={selectedCol === 0 ? "true" : "false"}
								>
									<span className="srOnly">{selectedTitle}</span>
								</th>
							</tr>
						</thead>

						<tbody>
							{ROWS.map((row, rIdx) => {
								const rowPos = rIdx === 0 ? "first" : rIdx === ROWS.length - 1 ? "last" : "mid";
								const cell = row.cells[selectedCol];
								const isSel = selectedCol === 0;

								return (
									<tr key={`m-${row.label}`}>
										<th scope="row" className="critCell">
											{row.label}
										</th>
										<td
											className={`optCell ${isSel ? "sel" : ""}`}
											data-selected={isSel ? "true" : "false"}
											data-rowpos={rowPos}
										>
											<CellContent cell={cell} />
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>

			<div className="ctaWrap">
				<a
					className="pd-btn pd-btn--primary"
					href="https://przyjazne-deklaracje.pl/kalkulatory/menadzer/menadzer-kalkulatorow?profileCode=O4B&templateCode=DN-1"
					aria-label="Przygotuj deklarację DN-1 ONLINE"
				>
					Przygotuj deklarację DN-1 ONLINE
				</a>
			</div>

			<p className="footnote">* Oszacowanie godzin dotyczy 10 przedmiotów opodatkowania.</p>
		</section>
	);
}
